import type { ReactNode } from "react";
import Button from "@/components/Button/Button";
import Dining from "@/components/SVGComponents/Dining";
import { combineClasses } from "@/lib/utils";
import styles from "./NoDataAvailable.module.scss";

interface NoDataAvailableProps {
  className?: string | undefined;
  title: string;
  description: ReactNode;
  note?: ReactNode | undefined;
  primaryActionLabel?: string | undefined;
  onPrimaryAction?: (() => void) | undefined;
  primaryActionIcon?: ReactNode | undefined;
  secondaryActionLabel?: string | undefined;
  onSecondaryAction?: (() => void) | undefined;
  secondaryActionIcon?: ReactNode | undefined;
}

const NoDataAvailable = ({
  className,
  title,
  description,
  note,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionIcon,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionIcon,
}: NoDataAvailableProps) => {
  const hasActions = Boolean(primaryActionLabel || secondaryActionLabel);

  return (
    <section className={combineClasses(styles.card, className)}>
      <div aria-hidden="true" className={styles.illustration}>
        <div className={styles.glowRing} />
        <div className={combineClasses(styles.spark, styles.sparkOne)} />
        <div className={combineClasses(styles.spark, styles.sparkTwo)} />
        <div className={combineClasses(styles.spark, styles.sparkThree)} />

        <div className={styles.lamp}>
          <span className={styles.lampCord} />
          <span className={styles.lampShade} />
          <span className={styles.lampBulb} />
        </div>

        <div className={styles.scene}>
          <div className={combineClasses(styles.chair, styles.chairLeft)}>
            <span className={styles.chairBack} />
            <span className={styles.chairSeat} />
            <span className={styles.chairLegs} />
          </div>

          <div className={styles.tableWrap}>
            <span className={styles.tableTop} />
            <span className={styles.tableCloth} />
            <span className={styles.tableLeg} />
            <span className={styles.tableBase} />
            <span className={styles.vase} />
            <span className={styles.flowerStem} />
            <span className={styles.flowerLeft} />
            <span className={styles.flowerRight} />
            <span className={styles.clocheBase} />
            <span className={styles.clocheCover} />
            <span className={styles.clocheHandle} />
          </div>

          <div className={combineClasses(styles.chair, styles.chairRight)}>
            <span className={styles.chairBack} />
            <span className={styles.chairSeat} />
            <span className={styles.chairLegs} />
          </div>
        </div>

        <span className={styles.floorLine} />
      </div>

      <div className={styles.copyBlock}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      {hasActions ? (
        <div className={styles.actions}>
          {primaryActionLabel ? (
            <Button
              className={styles.primaryAction}
              large
              onClick={onPrimaryAction}
              type="button"
            >
              {primaryActionIcon ? (
                <span className={styles.actionIcon}>{primaryActionIcon}</span>
              ) : null}
              <span>{primaryActionLabel}</span>
            </Button>
          ) : null}

          {secondaryActionLabel ? (
            <Button
              className={styles.secondaryAction}
              large
              onClick={onSecondaryAction}
              outlined
              type="button"
            >
              {secondaryActionIcon ? (
                <span className={styles.actionIcon}>{secondaryActionIcon}</span>
              ) : null}
              <span>{secondaryActionLabel}</span>
            </Button>
          ) : null}
        </div>
      ) : null}

      {note ? (
        <div className={styles.note}>
          <Dining className={styles.noteIcon} />
          <span>{note}</span>
        </div>
      ) : null}
    </section>
  );
};

export default NoDataAvailable;
